import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiGeneralParams, IApiPaginatedResponse } from "@/types";
import type {
  ICompanyPolicyCategoryRequest,
  ISingleCompanyPolicies,
  ISingleCompanyPoliciesCategory,
} from "@/types/(company)/company-policies.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const companyPoliciesApi = createApi({
  reducerPath: "companyPoliciesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "GetAllCompanyPolicies",
    "GetCompanyPoliciesStats",
    "GetCompanyPolicyById",
    "GetAllCompanyPoliciesCategories",
    "GetCompanyPoliciesCategoryById",
    "GetAllAcknowledgedCompanyPolicies",
  ],
  endpoints: (builder) => {
    const { CompanyPolicies } = APIEndpoints.Company;

    return {
      // ==============================
      // Policies
      // ==============================

      getAllCompanyPolicies: builder.query<
        IApiPaginatedResponse<ISingleCompanyPolicies[]>,
        IApiGeneralParams
      >({
        query: (params) => ({
          url: CompanyPolicies.GetAllCompanyPolicies,
          method: "GET",
          params,
        }),
        providesTags: ["GetAllCompanyPolicies"],
      }),
      getCompanyPolicyById: builder.query<
        IApiPaginatedResponse<ISingleCompanyPolicies>,
        { companyPoliciesId: string }
      >({
        query: ({ companyPoliciesId }) => ({
          url: CompanyPolicies.GetCompanyPolicyById(companyPoliciesId),
          method: "GET",
        }),
        providesTags: ["GetCompanyPolicyById"],
      }),

      createCompanyPolicy: builder.mutation<
        IApiPaginatedResponse<ISingleCompanyPolicies>,
        FormData
      >({
        query: (body) => ({
          url: CompanyPolicies.CreateCompanyPolicies,
          method: "POST",
          body,
        }),
        invalidatesTags: ["GetAllCompanyPolicies", "GetCompanyPoliciesStats"],
      }),

      updateCompanyPolicy: builder.mutation<
        IApiPaginatedResponse<ISingleCompanyPolicies>,
        { companyPoliciesId: string; body: FormData }
      >({
        query: ({ companyPoliciesId, body }) => ({
          url: CompanyPolicies.UpdateCompanyPolicyById(companyPoliciesId),
          method: "PUT",
          body,
        }),
        invalidatesTags: ["GetAllCompanyPolicies", "GetCompanyPoliciesStats"],
      }),

      publishCompanyPolicy: builder.mutation<
        IApiPaginatedResponse<void>,
        { companyPoliciesId: string }
      >({
        query: ({ companyPoliciesId }) => ({
          url: CompanyPolicies.PublishCompanyPolicies(companyPoliciesId),
          method: "POST",
        }),
        invalidatesTags: ["GetAllCompanyPolicies", "GetCompanyPoliciesStats"],
      }),

      withdrawCompanyPolicy: builder.mutation<
        IApiPaginatedResponse<void>,
        { companyPoliciesId: string }
      >({
        query: ({ companyPoliciesId }) => ({
          url: CompanyPolicies.WithdrawCompanyPolicyById(companyPoliciesId),
          method: "POST",
        }),
        invalidatesTags: ["GetAllCompanyPolicies", "GetCompanyPoliciesStats"],
      }),

      deleteCompanyPolicy: builder.mutation<
        IApiPaginatedResponse<void>,
        { companyPoliciesId: string }
      >({
        query: ({ companyPoliciesId }) => ({
          url: CompanyPolicies.DeleteCompanyPolicyById(companyPoliciesId),
          method: "DELETE",
        }),
        invalidatesTags: ["GetAllCompanyPolicies", "GetCompanyPoliciesStats"],
      }),

      getAllAcknowledgedCompanyPolicies: builder.query<
        IApiPaginatedResponse<void>,
        { companyPoliciesId: string } & IApiGeneralParams
      >({
        query: (params) => ({
          url: CompanyPolicies.GetAllCompanyPoliciesAcknowledgmentTracking(
            params.companyPoliciesId,
          ),
          method: "GET",
          params,
        }),
        providesTags: ["GetAllAcknowledgedCompanyPolicies"],
      }),

      // ==============================
      // Categories
      // ==============================

      getAllCompanyPoliciesCategories: builder.query<
        IApiPaginatedResponse<ISingleCompanyPoliciesCategory[]>,
        IApiGeneralParams
      >({
        query: (params) => ({
          url: CompanyPolicies.GetAllCompanyPoliciesCategories,
          method: "GET",
          params,
        }),
        providesTags: ["GetAllCompanyPoliciesCategories"],
      }),
      getAllPoliciesCategories: builder.infiniteQuery<
        IApiPaginatedResponse<ISingleCompanyPoliciesCategory[]>,
        { limit: number; isActive: boolean },
        number
      >({
        infiniteQueryOptions: {
          initialPageParam: 1,
          getNextPageParam: (lastPage) => {
            const { page, totalPages } = lastPage.meta;
            return page < totalPages ? page + 1 : undefined;
          },
        },
        query: ({ queryArg, pageParam }) => ({
          url: CompanyPolicies.GetAllCompanyPoliciesCategories,
          method: "GET",
          params: {
            limit: queryArg.limit,
            page: pageParam,
            isActive: queryArg.isActive,
          },
        }),
      }),

      getCompanyPoliciesCategoryById: builder.query<
        IApiPaginatedResponse<ISingleCompanyPoliciesCategory>,
        { categoryId: string }
      >({
        query: ({ categoryId }) => ({
          url: CompanyPolicies.GetCompanyPoliciesCategoryById(categoryId),
          method: "GET",
        }),
        providesTags: ["GetCompanyPoliciesCategoryById"],
      }),

      createCompanyPoliciesCategory: builder.mutation<
        IApiPaginatedResponse<ISingleCompanyPoliciesCategory>,
        Partial<ICompanyPolicyCategoryRequest>
      >({
        query: (body) => ({
          url: CompanyPolicies.CreateCompanyPoliciesCategories,
          method: "POST",
          body,
        }),
        invalidatesTags: ["GetAllCompanyPoliciesCategories"],
      }),

      updateCompanyPoliciesCategory: builder.mutation<
        IApiPaginatedResponse<ISingleCompanyPoliciesCategory>,
        { categoryId: string; body: Partial<ICompanyPolicyCategoryRequest> }
      >({
        query: ({ categoryId, body }) => ({
          url: CompanyPolicies.UpdateCompanyPoliciesCategory(categoryId),
          method: "PUT",
          body,
        }),
        invalidatesTags: ["GetAllCompanyPoliciesCategories"],
      }),

      deleteCompanyPoliciesCategory: builder.mutation<
        IApiPaginatedResponse<void>,
        { categoryId: string }
      >({
        query: ({ categoryId }) => ({
          url: CompanyPolicies.DeleteCompanyPoliciesCategory(categoryId),
          method: "DELETE",
        }),
        invalidatesTags: ["GetAllCompanyPoliciesCategories"],
      }),
    };
  },
});

export const {
  // Policies Hooks
  useGetAllCompanyPoliciesQuery,
  useGetCompanyPolicyByIdQuery,
  useCreateCompanyPolicyMutation,
  useUpdateCompanyPolicyMutation,
  usePublishCompanyPolicyMutation,
  useWithdrawCompanyPolicyMutation,
  useDeleteCompanyPolicyMutation,
  useGetAllAcknowledgedCompanyPoliciesQuery,

  // Categories Hooks
  useGetAllCompanyPoliciesCategoriesQuery,
  useGetAllPoliciesCategoriesInfiniteQuery,
  useGetCompanyPoliciesCategoryByIdQuery,
  useCreateCompanyPoliciesCategoryMutation,
  useUpdateCompanyPoliciesCategoryMutation,
  useDeleteCompanyPoliciesCategoryMutation,
} = companyPoliciesApi;
